import classNames from 'classnames';
import * as _ from 'lodash-es';
import React from 'react';
import * as styles from './Pill.module.scss';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';
import Link from '../utils/router/Link';

export type PillSize = 'small' | 'normal';

export const PILL_TYPES = [
  'facet',
  'user',
  'post',
  'topic',
  'series',
  'group',
  'external',
  'attachment',
  'template',
] as const;

export type PillType = (typeof PILL_TYPES)[number];

interface PillProps
  extends Pick<React.HTMLAttributes<HTMLSpanElement>, 'tabIndex'> {
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconAfter?: React.ReactNode;
  selected?: boolean;
  muted?: boolean;
  size?: PillSize;
  type: PillType;
  onClick?: React.MouseEventHandler;
  className?: string;
  to?: React.ComponentProps<typeof Link>['to'] | null;
  newTab?: React.ComponentProps<typeof Link>['newTab'];
  noWrap?: boolean;
  innerHeadRef?: React.Ref<HTMLSpanElement>;
}

const ICON_SIZES: Record<PillSize, SVGIconSize> = {
  small: 14,
  normal: 'regular',
};

const Pill = ({
  icon,
  iconAfter,
  children,
  selected = false,
  muted = false,
  size = 'normal',
  type,
  onClick,
  className,
  to,
  noWrap,
  newTab,
  innerHeadRef,
  ...spanProps
}: PillProps) => {
  const iconSize = ICON_SIZES[size];

  const wrapIcon = (inner: string | React.ReactNode) => {
    if (typeof inner === 'string') {
      return (
        <span data-no-print className={styles.icon} aria-label="icon">
          <SVGIcon inline size={iconSize}>
            {inner}
          </SVGIcon>
        </span>
      );
    }
    if (!!inner && typeof inner !== 'string') {
      return (
        <span data-no-print className={styles.icon} aria-label="icon">
          {inner}
        </span>
      );
    }
    return null;
  };

  const getInnerContent = () => {
    if (!noWrap && typeof children === 'string') {
      const chars = _.split(children, '');
      const firstLetter = chars.shift();
      const lastLetter = chars.pop();
      const middleContent = chars.join('');

      return (
        <>
          {!!icon && (
            <span className={styles.innerHead} ref={innerHeadRef}>
              {wrapIcon(icon)}
              {firstLetter}
            </span>
          )}
          <span>
            {!icon && firstLetter}
            {middleContent}
            {!iconAfter && lastLetter}
          </span>
          {!!iconAfter && (
            <span className={styles.innerTail}>
              {lastLetter}
              {wrapIcon(iconAfter)}
            </span>
          )}
        </>
      );
    }

    return (
      <>
        {!!icon && (
          <span className={styles.innerHead} ref={innerHeadRef}>
            {wrapIcon(icon)}
          </span>
        )}
        <span>{children}</span>
        {!!iconAfter && (
          <span className={styles.innerTail}>{wrapIcon(iconAfter)}</span>
        )}
      </>
    );
  };

  const inner = (
    <span
      {...spanProps}
      className={classNames(
        styles.container,
        {
          [styles.small]: size === 'small',
          [styles.normal]: size === 'normal',
          [styles.selected]: selected,
          [styles.muted]: muted,
          [styles.facet]: type === 'facet',
          [styles.user]: type === 'user',
          [styles.post]: type === 'post',
          [styles.topic]: type === 'topic',
          [styles.series]: type === 'series',
          [styles.group]: type === 'group',
          [styles.external]: type === 'external',
          [styles.template]: type === 'template',
          [styles.attachment]: type === 'attachment',
          [styles.noWrap]: noWrap,
        },
        className,
      )}
    >
      {getInnerContent()}
    </span>
  );

  if (to) {
    return (
      <Link to={to} newTab={newTab} onClick={onClick}>
        {inner}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button className={styles.button} onClick={onClick} type="button">
        {inner}
      </button>
    );
  }

  return inner;
};

export default Pill;
